import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Languages, Loader2, CheckCircle, AlertCircle, Globe, Sparkles, RefreshCw, Eye } from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const AdminTranslateTab = ({ getAuthHeaders }) => {
  const [stats, setStats] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState(['de', 'es', 'fr', 'zh', 'ru', 'it', 'pl', 'sv', 'pt', 'nl', 'tr', 'ar', 'hi', 'cs', 'ro', 'el', 'da', 'no', 'fi', 'bg', 'hr', 'sk', 'sr', 'hu', 'uk']);
  const [includeUpdated, setIncludeUpdated] = useState(true);
  const [smartMode, setSmartMode] = useState(true);

  // All 25 supported languages for translation
  const languages = [
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pl', name: 'Polish', flag: '🇵🇱' },
    { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'nl', name: 'Dutch', flag: '🇳🇱' },
    { code: 'tr', name: 'Turkish', flag: '🇹🇷' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
    { code: 'cs', name: 'Czech', flag: '🇨🇿' },
    { code: 'ro', name: 'Romanian', flag: '🇷🇴' },
    { code: 'el', name: 'Greek', flag: '🇬🇷' },
    { code: 'da', name: 'Danish', flag: '🇩🇰' },
    { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
    { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
    { code: 'bg', name: 'Bulgarian', flag: '🇧🇬' },
    { code: 'hr', name: 'Croatian', flag: '🇭🇷' },
    { code: 'sk', name: 'Slovak', flag: '🇸🇰' },
    { code: 'sr', name: 'Serbian', flag: '🇷🇸' },
    { code: 'hu', name: 'Hungarian', flag: '🇭🇺' },
    { code: 'uk', name: 'Ukrainian', flag: '🇺🇦' }
  ];

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/translate/stats`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch translation stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API}/translate/progress`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setProgress(data);
        
        // If still running, poll again
        if (data.status === 'running' || data.status === 'starting') {
          setTimeout(fetchProgress, 2000);
        } else if (data.status === 'completed') {
          setTranslating(false);
          fetchStats(); // Refresh stats
          toast.success(`Translation completed! ${data.completed} questions translated.`);
        } else if (data.status === 'failed') {
          setTranslating(false);
          toast.error('Translation failed. Check console for details.');
        }
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchProgress();
    analyzeTranslationNeeds();
  }, []);

  const analyzeTranslationNeeds = async () => {
    setAnalyzing(true);
    try {
      const response = await fetch(`${API}/translate/analyze`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      }
    } catch (error) {
      console.error('Failed to analyze translation needs:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleLanguageToggle = (code) => {
    setSelectedLanguages(prev => 
      prev.includes(code) 
        ? prev.filter(l => l !== code)
        : [...prev, code]
    );
  };

  const startTranslation = async (dryRun = false) => {
    if (selectedLanguages.length === 0) {
      toast.error('Bitte mindestens eine Sprache auswählen');
      return;
    }

    setTranslating(true);
    
    try {
      const endpoint = smartMode ? `${API}/translate/smart` : `${API}/translate/start`;
      const body = smartMode 
        ? {
            target_languages: selectedLanguages,
            include_updated: includeUpdated,
            dry_run: dryRun
          }
        : {
            target_languages: selectedLanguages
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        
        if (dryRun) {
          const questions = data.preview?.unique_questions_affected || 0;
          const translations = data.preview?.total_translations_needed || 0;
          toast.success(`Vorschau: ${questions} Fragen × ${selectedLanguages.length} Sprachen = ${translations} Übersetzungen`);
          setTranslating(false);
        } else if (data.status === 'complete') {
          toast.success('Alle Fragen sind bereits übersetzt!');
          setTranslating(false);
        } else {
          const questions = data.preview?.unique_questions_affected || '?';
          toast.success(`Übersetzung gestartet: ${questions} Fragen in ${selectedLanguages.length} Sprachen`);
          fetchProgress();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Übersetzung konnte nicht gestartet werden');
        setTranslating(false);
      }
    } catch (error) {
      toast.error('Übersetzung konnte nicht gestartet werden');
      setTranslating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  const isRunning = progress?.status === 'running' || progress?.status === 'starting';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="w-6 h-6 text-[#0047AB]" />
            Auto-Translate Questions
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Automatically translate questions from English to multiple languages using AI
          </p>
        </div>
      </div>

      {/* Translation Coverage Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="w-5 h-5" />
            Translation Coverage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats && languages.map(lang => {
              const langStats = stats.languages?.[lang.code];
              const percentage = langStats?.percentage || 0;
              
              return (
                <div key={lang.code} className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="flex items-center gap-2 font-medium">
                      <span className="text-xl">{lang.flag}</span>
                      {lang.name}
                    </span>
                    <span className={`text-sm font-bold ${percentage === 100 ? 'text-green-600' : 'text-orange-600'}`}>
                      {percentage}%
                    </span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                  <p className="text-xs text-slate-500 mt-2">
                    {langStats?.translated || 0} / {stats.total_questions || 0} questions
                  </p>
                </div>
              );
            })}
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm text-blue-700 dark:text-blue-300">
            <strong>Total Questions:</strong> {stats?.total_questions || 0}
          </div>
        </CardContent>
      </Card>

      {/* Translation Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#0047AB]" />
            Smart Translation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Smart Mode Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
            <div>
              <p className="font-medium">Smart Mode</p>
              <p className="text-sm text-slate-500">Only translate what's missing or outdated</p>
            </div>
            <Switch
              checked={smartMode}
              onCheckedChange={setSmartMode}
            />
          </div>

          {smartMode && (
            <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
              <div>
                <p className="font-medium flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Include Updated Questions
                </p>
                <p className="text-sm text-slate-500">Re-translate when original English text was changed</p>
              </div>
              <Switch
                checked={includeUpdated}
                onCheckedChange={setIncludeUpdated}
              />
            </div>
          )}

          {/* Analysis Summary */}
          {smartMode && analysis && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl space-y-2">
              <p className="font-medium text-blue-700 dark:text-blue-300">Translation Analysis</p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center p-2 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{analysis.summary?.fully_translated || 0}</p>
                  <p className="text-slate-500">Fully Translated</p>
                </div>
                <div className="text-center p-2 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{analysis.summary?.partially_translated || 0}</p>
                  <p className="text-slate-500">Partially Translated</p>
                </div>
                <div className="text-center p-2 bg-white dark:bg-slate-800 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{analysis.summary?.not_translated || 0}</p>
                  <p className="text-slate-500">Not Translated</p>
                </div>
              </div>
            </div>
          )}

          {/* Language Selection */}
          <div>
            <p className="text-sm font-medium mb-3">Select target languages:</p>
            <div className="flex flex-wrap gap-3">
              {languages.map(lang => {
                const langAnalysis = analysis?.languages?.[lang.code];
                const missing = langAnalysis?.missing || 0;
                const outdated = langAnalysis?.outdated || 0;
                
                return (
                  <label 
                    key={lang.code}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedLanguages.includes(lang.code)
                        ? 'border-[#0047AB] bg-[#0047AB]/5'
                        : 'border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    <Checkbox
                      checked={selectedLanguages.includes(lang.code)}
                      onCheckedChange={() => handleLanguageToggle(lang.code)}
                    />
                    <span className="text-lg">{lang.flag}</span>
                    <span>{lang.name}</span>
                    {smartMode && (missing > 0 || outdated > 0) && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                        {missing > 0 && `${missing} missing`}
                        {missing > 0 && outdated > 0 && ', '}
                        {outdated > 0 && `${outdated} outdated`}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Progress Display */}
          {isRunning && progress && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#0047AB]" />
                <span className="font-medium">Übersetzung läuft...</span>
              </div>
              <Progress value={(progress.completed / progress.total) * 100} className="h-2 mb-2" />
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {progress.completed} / {progress.total} Übersetzungen abgeschlossen
                {progress.failed > 0 && ` (${progress.failed} fehlgeschlagen)`}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Jede Frage wird in {selectedLanguages.length} Sprache{selectedLanguages.length !== 1 ? 'n' : ''} übersetzt
              </p>
            </div>
          )}

          {/* Completed Status */}
          {progress?.status === 'completed' && (
            <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-xl flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700 dark:text-green-400">
                Übersetzung abgeschlossen: {progress.completed} Übersetzungen erfolgreich
              </span>
            </div>
          )}

          {/* Error Status */}
          {progress?.status === 'failed' && (
            <div className="p-4 bg-red-50 dark:bg-red-950/30 rounded-xl flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 dark:text-red-400">
                Translation failed. Please try again.
              </span>
            </div>
          )}

          {/* Start Button */}
          <div className="flex gap-3">
            {smartMode && (
              <Button
                onClick={() => startTranslation(true)}
                disabled={translating || selectedLanguages.length === 0 || analyzing}
                variant="outline"
                className="flex-1"
                data-testid="preview-translation-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
            )}
            <Button
              onClick={() => startTranslation(false)}
              disabled={translating || selectedLanguages.length === 0}
              className={`${smartMode ? 'flex-1' : 'w-full'} bg-[#0047AB] hover:bg-[#003380] text-white`}
              data-testid="start-translation-btn"
            >
              {translating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  {smartMode ? 'Start Smart Translation' : 'Translate All Missing'}
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-slate-500 text-center">
            {smartMode 
              ? 'Smart mode only translates questions that are missing translations or have been updated since their last translation.'
              : 'This will translate all questions that don\'t have translations in the selected languages.'
            }
            {' '}The process runs in the background and may take several minutes.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTranslateTab;
